/**
 * Pointer 事件 DOM 薄层（engine 层，通用）：唯一碰 `PointerEvent`/SVG 几何 API 的文件
 * （照抄 chrome-chemistry pointer-controller.ts 的分工）。核心拖拽判定逻辑都在
 * `drag-controller.ts` 的纯函数里，这里只做"屏幕坐标→SVG 世界坐标"转换和事件订阅/清理。
 *
 * jsdom 不实现 `SVGSVGElement.createSVGPoint`/`getScreenCTM`（已验证），所以两处都做了
 * try/catch + 特性探测降级，保证测试环境下不抛错（退化为屏幕坐标，测试用相对位移断言，
 * 不依赖真实缩放）。
 */

import type { Point } from "./drag-controller.js";

export function toWorldPoint(svg: SVGSVGElement, clientX: number, clientY: number): Point {
  try {
    if (typeof svg.createSVGPoint === "function" && typeof svg.getScreenCTM === "function") {
      const point = svg.createSVGPoint();
      point.x = clientX;
      point.y = clientY;
      const ctm = svg.getScreenCTM();
      if (ctm) {
        const mapped = point.matrixTransform(ctm.inverse());
        return { x: mapped.x, y: mapped.y };
      }
    }
  } catch {
    // 忽略：降级到屏幕坐标
  }
  const rect = svg.getBoundingClientRect();
  return { x: clientX - rect.left, y: clientY - rect.top };
}

export interface DragHandlers {
  onStart?: (point: Point, event: PointerEvent) => void;
  onMove: (point: Point, event: PointerEvent) => void;
  onEnd: (point: Point, event: PointerEvent) => void;
}

/**
 * 在 `source` 元素上绑定一次性拖拽会话：pointerdown 起手，move/up 监听挂在 window
 * （即使指针滑出源元素也能跟手）。返回取消订阅函数。
 */
export function bindDragSource(source: Element, svg: SVGSVGElement, handlers: DragHandlers): () => void {
  function onPointerDown(rawEvent: Event): void {
    const event = rawEvent as PointerEvent;
    const pointerId = event.pointerId;
    try {
      (source as Element & { setPointerCapture?: (id: number) => void }).setPointerCapture?.(pointerId);
    } catch {
      // 不支持 Pointer Capture 的环境（如 jsdom）：继续监听 window，只是滑出源元素外
      // 可能丢事件——桌面场景下影响很小。
    }
    handlers.onStart?.(toWorldPoint(svg, event.clientX, event.clientY), event);

    function onMove(rawMove: Event): void {
      const moveEvent = rawMove as PointerEvent;
      if (moveEvent.pointerId !== pointerId) return;
      handlers.onMove(toWorldPoint(svg, moveEvent.clientX, moveEvent.clientY), moveEvent);
    }
    function onUp(rawUp: Event): void {
      const upEvent = rawUp as PointerEvent;
      if (upEvent.pointerId !== pointerId) return;
      cleanup();
      handlers.onEnd(toWorldPoint(svg, upEvent.clientX, upEvent.clientY), upEvent);
    }
    function onCancel(rawCancel: Event): void {
      const cancelEvent = rawCancel as PointerEvent;
      if (cancelEvent.pointerId !== pointerId) return;
      cleanup();
    }
    function cleanup(): void {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  }

  source.addEventListener("pointerdown", onPointerDown);
  return () => source.removeEventListener("pointerdown", onPointerDown);
}
